// Mirrors iOS RestaurantOrderHistorySection + RestaurantOrderHistoryRow:
// "Order History" header with its own segmented timeframe picker, rows show
// order number, customer, date, status badge and the restaurant payout in
// green. Tapping a row navigates to the order detail page.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { SegmentedPicker, StatusBadge } from '../../shared/ui';
import { formatCurrency, formatDate } from '../../shared/models';
import { TIMEFRAMES, ordersInTimeframe, payoutOf } from './analyticsUtils';

function OrderHistoryRow({ order }) {
  const navigate = useNavigate();
  const itemCount = (order.orderItems || []).reduce((s, i) => s + (i.quantity || 1), 0);
  return (
    <button
      onClick={() => navigate(`/business/orders/${order.id}`)}
      className="w-full text-left bg-gray-50/70 hover:bg-gray-100 rounded-lg p-3 transition-colors"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 truncate">
          #{String(order.orderNumber || order.id).slice(0, 8)}
        </span>
        <StatusBadge status={order.status} />
      </div>
      <div className="flex items-end justify-between gap-2 mt-1">
        <div className="min-w-0">
          <p className="text-sm text-gray-500 truncate">{order.customerName || 'Customer'}</p>
          <p className="text-[10px] text-gray-400">{formatDate(order.createdAt)}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-green-600">{formatCurrency(payoutOf(order))}</p>
          <p className="text-[10px] text-gray-400">{itemCount} items · {order.orderType}</p>
        </div>
      </div>
    </button>
  );
}

export default function OrderHistorySection({ orders }) {
  const [timeframe, setTimeframe] = useState('year');
  const filtered = useMemo(() => ordersInTimeframe(orders, timeframe), [orders, timeframe]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 py-4">
      <div className="flex items-center justify-between px-4 gap-2">
        <h3 className="text-sm font-bold text-gray-900 shrink-0">Order History</h3>
        <div className="w-52">
          <SegmentedPicker options={TIMEFRAMES} value={timeframe} onChange={setTimeframe} />
        </div>
      </div>

      <div className="mt-3 px-4 space-y-2">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center gap-2">
            <Inbox size={36} className="text-gray-300" />
            <p className="font-semibold text-gray-500 text-sm">No orders found</p>
            <p className="text-xs text-gray-400">Orders from the selected time period will appear here.</p>
          </div>
        ) : (
          <>
            {filtered.slice(0, 20).map((o) => <OrderHistoryRow key={o.id} order={o} />)}
            {filtered.length > 20 && (
              <p className="text-[11px] text-gray-400 text-center py-2">
                Showing 20 of {filtered.length} orders
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
